import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AsyncHandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {toggleHideTagAction} from '../../../../store/data/preferences/preferencesActions';
import {getVisibleCustomViewIds} from '../../../../store/data/preferences/preferencesSelectors';
import {saveTagAction} from '../../../../store/data/tags/saveTagActions';
import {getTags} from '../../../../store/data/tags/tagsSelectors';
import {
  getFlatActiveOrPendingTeammates,
  getUserId,
  getUserNamespace,
} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace} from '../../../../store/data/teams/teamsSelectors';
import {
  hideWorkspaceTagAsyncAction,
  showWorkspaceTagAsyncAction,
} from '../../../../store/data/workspace/workspaceActions';
import {EditTagDropdown} from './editTagDropdown';
import {TagTemplate} from './tagEditorBody';

/*
 * Props.
 */

interface EditTagDropdownContainerProps {
  tag: TagModel;
}

/*
 * Selectors.
 */

function getTag(_state: AppState, props: EditTagDropdownContainerProps) {
  return props.tag;
}

function makeGetParentTag() {
  return createSelector([getTag, getTags], (tag, tags) => {
    const {parentTagId} = tag;
    if (!parentTagId) {
      return undefined;
    }

    const parentTag = tags[parentTagId];
    if (!parentTag) {
      throw new Error('The parent tag could not be found.');
    }

    return parentTag;
  });
}

function makeGetIsVisibleCustomView() {
  return createSelector([getVisibleCustomViewIds, getTag], (visibleCustomViewIds, tag) =>
    visibleCustomViewIds.includes(tag.id),
  );
}

function makeGetTagTeam() {
  return createSelector(
    [getTag, getTeamsByNamespace],
    (tag, teamNamespaces) => teamNamespaces[tag.namespace],
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<EditTagDropdownContainerProps>()(() => ({
  userId: getUserId,
  userNamespace: getUserNamespace,
  parentTag: makeGetParentTag(),
  teammates: getFlatActiveOrPendingTeammates,
  hasPermission: getHasPermission,
  isVisibleCustomView: makeGetIsVisibleCustomView(),
  team: makeGetTagTeam(),
}));

export const EditTagDropdownContainer = connect(
  mapStateToProps,
  {
    saveTag: saveTagAction,
    toggleHideTag: toggleHideTagAction,
    showWorkspaceTag: showWorkspaceTagAsyncAction,
    hideWorkspaceTag: hideWorkspaceTagAsyncAction,
  },
  (stateProps, dispatchProps, ownProps) => {
    const updateTagAsync: AsyncHandlerOf<TagTemplate> = async (template) => {
      await dispatchProps.saveTag(ownProps.tag.id, {
        newName: template.name,
        newType: template.highlightType,
        newParentTags: template.parentTags,
        newHighlightEmoji: template.highlightEmoji,
        newHighlightColor: template.highlightColor,
        newShowInCells: template.showInCells,
      });

      // If toggling tag visibility in workspace, toggle it in the workspace resource:
      if (template.showInWorkspaces) {
        dispatchProps.showWorkspaceTag(ownProps.tag.id, ownProps.tag.isView, ownProps.tag.namespace);
      } else {
        dispatchProps.hideWorkspaceTag(ownProps.tag.id, ownProps.tag.isView, ownProps.tag.namespace);
      }
      // If toggling tag visibility in workspace, toggle it in the user's preferences:
      dispatchProps.toggleHideTag(ownProps.tag.id, [ownProps.tag], {value: template.showInWorkspaces});
    };

    return {
      ...stateProps,
      ...ownProps,
      updateTagAsync,
    };
  },
)(EditTagDropdown);
